import { z } from 'zod';
import { IsoDateRangeCodec, IsoDatetimeToDateSchema, NullableIsoDateCodec } from './date-codecs.js';
import { VerifiedTypeSchema } from './followers.js';
import { UuidSchema } from './primitives/uuid.js';
import { CursorPaginationSchema, PaginationPageHasMoreSchema } from './shared/pagination.js';
import { CanonicalTweetSchema } from './tweets-canonical.js';
import { TwitterApiTweetSchema } from './twitter-api.js';

export { TimelineErrorSchema } from './shared/errors.js';

import { TIME_RANGE_VALUES, TimeRangeSchema } from './time-range.js';
import toArray from './utils/arrayish.js';

export { TIME_RANGE_VALUES } from './time-range.js';

export const SORT_VALUES = ['latest', 'views', 'likes', 'bookmarks'] as const;
export const TIMELINE_RESPONSE_FORMATS = ['json', 'cli', 'csv', 'markdown', 'pdf'] as const;
export const TIMELINE_VARIANTS = ['standard', 'algorithmic'] as const;

export const TimelineResponseFormatSchema = z.enum(TIMELINE_RESPONSE_FORMATS);
export type TimelineResponseFormat = (typeof TIMELINE_RESPONSE_FORMATS)[number];

export const TimelineRequestSchema = z
  .object({
    type: z.enum(['home', 'user', 'search', 'list']).default('home'),
    userId: UuidSchema.optional(),
    listId: z.string().optional(),
    cursor: z.string().nullable().optional(),
    limit: z.number().min(1).max(100).default(30),
    maxResults: z.number().optional(),
    query: z.string().optional(),
    excludeReplies: z.boolean().optional(),
    excludeRetweets: z.boolean().optional(),
    timeRange: z.enum(TIME_RANGE_VALUES).optional(),
    sort: z.enum(SORT_VALUES).optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    freshness: z.enum(['cache-first', 'fresh', 'auto']).default('auto').optional(),
    format: TimelineResponseFormatSchema.optional(),
    variant: z.enum(TIMELINE_VARIANTS).default('standard').optional(),
  })
  .refine((data) => data.type !== 'user' || data.userId, { message: 'userId required for user timeline' })
  .refine((data) => data.type !== 'search' || data.query, { message: 'query required for search timeline' })
  .refine((data) => data.type !== 'list' || data.listId, { message: 'listId required for list timeline' });

// Parsed server-side filters derived from TimelineRequest before persistence.
export const TimelineFilterInputSchema = z
  .object({
    includeReplies: z.boolean().optional(),
    includeRetweets: z.boolean().optional(),
    timeRange: z.enum(TIME_RANGE_VALUES).optional(),
    sort: z.enum(SORT_VALUES).optional(),
    cursor: z.string().nullable().optional(),
    limit: z.number().min(1).max(100).optional(),
    untilId: z.string().optional(),
    sinceId: z.string().optional(),
  })
  .passthrough();

// Canonical page payload returned by timeline fetch APIs and persisted in caches.
export const TimelineMentionProfileSchema = z
  .object({
    profileId: z.string().optional().nullable(),
    username: z.string(),
    name: z.string().optional().nullable(),
    profileImageUrl: z.string().optional().nullable(),
  })
  .strip();

export const TimelinePageResponseSchema = z.object({
  timelineTweetIds: z.array(z.string()),
  tweets: z.record(z.string(), z.unknown()),
  profiles: z.record(z.string(), z.unknown()),
  mentionProfiles: z.record(z.string(), TimelineMentionProfileSchema).optional(),
  urlMetadata: z.record(z.string(), z.unknown()),
  media: z.record(z.string(), z.unknown()),
  inlineTweets: z.record(z.string(), z.unknown()).optional(),
  pagination: CursorPaginationSchema.extend({
    limit: z.number().int().min(1).max(100).nullable().optional(),
    offset: z.number().int().min(0).nullable().optional(),
  }),
  source: z.enum(['twitter-api', 'database', 'search']),
  timestamp: z.date().optional(),
});

// Normalized map produced by TweetProcessor and consumed by realtime + exports.
export const TimelineNormalizedResponseSchema = z.object({
  tweetIds: z.array(z.string()),
  tweets: z.record(z.string(), z.unknown()),
  profiles: z.record(z.string(), z.unknown()),
  mentionProfiles: z.record(z.string(), TimelineMentionProfileSchema).optional(),
  urlMetadata: z.record(z.string(), z.unknown()),
  media: z.record(z.string(), z.unknown()),
});

// Augmented timeline payload exposed by /api/timeline responses.
export const TimelineResponsePayloadSchema = TimelinePageResponseSchema.extend({
  debug: z.record(z.string(), z.unknown()).optional(),
});

// REST/GraphQL includes bundle returned alongside timeline tweets.
export const TimelineMediaVariantSchema = z
  .object({
    url: z.string().url().optional(),
    bit_rate: z.number().optional(),
    content_type: z.string().optional(),
  })
  .strip();

export const TimelineMediaObjectSchema = z
  .object({
    media_key: z.string(),
    type: z.enum(['photo', 'video', 'animated_gif']),
    url: z.string().url().optional(),
    preview_image_url: z.string().url().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    duration_ms: z.number().optional(),
    alt_text: z.string().optional(),
    variants: z.array(TimelineMediaVariantSchema).optional(),
  })
  .strip();

/**
 * We accept only snake_case media inputs from upstream boundaries.
 * GraphQL adapters must downcase field names before invoking these helpers.
 */
export const TimelineMediaVariantInputSchema = z
  .object({
    url: z.string().url().optional().nullable(),
    bit_rate: z.number().optional().nullable(),
    content_type: z.string().optional().nullable(),
  })
  .passthrough()
  .superRefine((value, ctx) => {
    if (value && typeof value === 'object' && 'bitRate' in value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bitRate'],
        message: 'Timeline media variants must use snake_case (bit_rate).',
      });
    }
    if (value && typeof value === 'object' && 'contentType' in value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentType'],
        message: 'Timeline media variants must use snake_case (content_type).',
      });
    }
  })
  .transform((value) => {
    const normalized: Record<string, unknown> = {
      url: value?.url ?? undefined,
      bit_rate: value?.bit_rate ?? undefined,
      content_type: value?.content_type ?? undefined,
    };
    return normalized as z.input<typeof TimelineMediaVariantSchema>;
  })
  .pipe(TimelineMediaVariantSchema);

export const TimelineMediaObjectInputSchema = z
  .object({
    media_key: z.string().optional().nullable(),
    type: z.enum(['photo', 'video', 'animated_gif']).optional().nullable(),
    url: z.string().url().optional().nullable(),
    preview_image_url: z.string().url().optional().nullable(),
    width: z.number().optional().nullable(),
    height: z.number().optional().nullable(),
    duration_ms: z.number().optional().nullable(),
    alt_text: z.string().optional().nullable(),
    variants: z.array(TimelineMediaVariantInputSchema).optional().nullable(),
  })
  .passthrough()
  .superRefine((value, ctx) => {
    const camelKeys: [string, string][] = [
      ['mediaKey', 'media_key'],
      ['previewImageUrl', 'preview_image_url'],
      ['durationMs', 'duration_ms'],
      ['altText', 'alt_text'],
    ];

    for (const [camelKey, expected] of camelKeys) {
      if (value && typeof value === 'object' && camelKey in value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [camelKey],
          message: `Timeline media objects must use snake_case (${expected}).`,
        });
      }
    }
  })
  .transform((value) => {
    const source = value ?? {};
    const normalized: Record<string, unknown> = {
      media_key: source.media_key ?? undefined,
      type: source.type ?? undefined,
      url: source.url ?? undefined,
      preview_image_url: source.preview_image_url ?? undefined,
      width: source.width ?? undefined,
      height: source.height ?? undefined,
      duration_ms: source.duration_ms ?? undefined,
      alt_text: source.alt_text ?? undefined,
      variants: source.variants ?? undefined,
    };
    return normalized;
  })
  .pipe(TimelineMediaObjectSchema);

export const TimelineIncludesSchema = z.object({
  users: z.array(z.record(z.string(), z.unknown())).optional(),
  tweets: z.array(z.record(z.string(), z.unknown())).optional(),
  media: z
    .union([
      z.array(TimelineMediaObjectInputSchema),
      z.record(z.string(), TimelineMediaObjectInputSchema),
      z.map(z.string(), TimelineMediaObjectInputSchema),
    ])
    .optional(),
});

// Wrapper pushed over realtime channels when timeline pages stream to clients.
export const TimelineRealtimeEnvelopeSchema = z.object({
  page: TimelineResponsePayloadSchema,
  receivedAt: z.date(),
});

export type TimelineMediaCandidate = TimelineMediaInput | TimelineMediaObject | null | undefined;
export type TimelineMediaCollectionCandidate =
  | readonly TimelineMediaCandidate[]
  | Record<string, TimelineMediaCandidate>
  | Map<string, TimelineMediaCandidate>
  | null
  | undefined;

export function toTimelineMediaObject(candidate: TimelineMediaCandidate): TimelineMediaObject | null {
  if (candidate == null) {
    return null;
  }

  const parsed = TimelineMediaObjectInputSchema.safeParse(candidate);
  if (parsed.success) {
    return parsed.data;
  }

  if (typeof candidate !== 'object') {
    return null;
  }

  return null;
}

const isTimelineMediaRecord = (value: unknown): value is Record<string, TimelineMediaCandidate> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

export function normalizeTimelineMediaCollection(collection: TimelineMediaCollectionCandidate): TimelineMediaObject[] {
  let candidates: readonly TimelineMediaCandidate[] = [];
  if (collection instanceof Map) {
    candidates = [...collection.values()];
  } else if (Array.isArray(collection)) {
    candidates = collection;
  } else if (isTimelineMediaRecord(collection)) {
    candidates = Object.values(collection);
  }

  const normalized: TimelineMediaObject[] = [];
  for (const candidate of candidates) {
    const parsed = toTimelineMediaObject(candidate);
    if (parsed) {
      normalized.push(parsed);
    }
  }
  return normalized;
}

export function timelineMediaFromArray(collection: readonly TimelineMediaInput[]): TimelineMediaObject[] {
  return collection
    .map((item) => toTimelineMediaObject(item))
    .filter((item): item is TimelineMediaObject => item !== null);
}

export const MentionsTimelineResponseSchema = z.object({
  tweets: z.array(TwitterApiTweetSchema),
  pagination: PaginationPageHasMoreSchema.extend({
    cursor: z.string().nullable(),
  }).partial({
    page: true,
    totalCount: true,
  }),
});

export const UserStatusResponseSchema = z.object({
  user: z
    .object({
      id: z.string(),
      username: z.string(),
      name: z.string(),
      profileImageUrl: z.string().url().nullable().optional(),
      verifiedType: z.enum(['none', 'blue', 'business', 'government']),
      createdAt: z.date(),
      updatedAt: z.date(),
      lastFetchedAt: z.date(),
      followersCount: z.number(),
      followingCount: z.number(),
      tweetCount: z.number(),
      listedCount: z.number(),
      description: z.string().nullable().optional(),
      location: z.string().nullable().optional(),
      url: z.string().nullable().optional(),
      isFollowing: z.boolean().optional(),
      followsYou: z.boolean().optional(),
    })
    .nullable(),
  tweetStats: z.object({
    totalInDatabase: z.number(),
    dateRange: IsoDateRangeCodec,
    lastSyncedAt: NullableIsoDateCodec,
    needsSync: z.boolean(),
  }),
  isAuthenticated: z.boolean(),
  isStale: z.boolean().optional(),
});

// Raw snapshot row shape pulled from the database (includes metadata).
export const TimelineSnapshotResponseSchema = z.object({
  id: z.string(),
  userId: UuidSchema,
  tweetIds: z.array(z.string()),
  tweetCount: z.number(),
  earliestTweetAt: z.date(),
  latestTweetAt: z.date(),
  metadata: z.unknown().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
});

// Simplified snapshot status returned to callers fetching cache state.
export const TimelineSnapshotStatusSchema = z.object({
  tweetIds: z.array(z.string()),
  updatedAt: z.date(),
  needsSync: z.boolean(),
});

export const TimelineCliTweetSchema = CanonicalTweetSchema;

export const TimelineCliPaginationSchema = z.object({
  cursor: z.string().nullable(),
  prevCursor: z.string().nullable().optional(),
  hasNextPage: z.boolean(),
  hasPrevPage: z.boolean(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  totalCount: z.number().optional(),
  totalPages: z.number().optional(),
});

export const TimelineCliSummarySchema = z.object({
  tweetCount: z.number(),
  uniqueAuthors: z.number(),
  totals: z.object({
    likes: z.number(),
    retweets: z.number(),
    replies: z.number(),
    quotes: z.number(),
    bookmarks: z.number(),
    impressions: z.number(),
  }),
  timeWindow: z
    .object({
      start: IsoDatetimeToDateSchema,
      end: IsoDatetimeToDateSchema,
    })
    .nullable(),
});

export const TimelineCliResponseSchema = z.object({
  tweets: z.array(TimelineCliTweetSchema),
  pagination: TimelineCliPaginationSchema,
  summary: TimelineCliSummarySchema,
  filters: z.object({
    limit: z.number(),
    sort: z.enum(SORT_VALUES),
    includeRetweets: z.boolean(),
    includeReplies: z.boolean(),
    timeRange: z.enum(TIME_RANGE_VALUES).nullable(),
    cursor: z.string().nullable(),
  }),
  timelineType: z.enum(['home', 'mentions']).optional(),
});

const TimelineFetchJsonResponseSchema = z.object({
  format: z.literal('json'),
  contentType: z.literal('application/json'),
  body: TimelinePageResponseSchema,
});

const TimelineFetchCliResponseSchema = z.object({
  format: z.literal('cli'),
  contentType: z.literal('application/json'),
  body: TimelineCliResponseSchema,
});

const TimelineFetchTextResponseSchema = z.object({
  format: z.enum(['csv', 'markdown']),
  contentType: z.string(),
  body: z.string(),
  fileName: z.string(),
  encoding: z.literal('utf8').optional(),
});

const TimelineFetchBinaryResponseSchema = z.object({
  format: z.literal('pdf'),
  contentType: z.string(),
  body: z.string(),
  fileName: z.string(),
  encoding: z.literal('base64'),
});

export const TimelineFetchResponseSchema = z.union([
  TimelineFetchJsonResponseSchema,
  TimelineFetchCliResponseSchema,
  TimelineFetchTextResponseSchema,
  TimelineFetchBinaryResponseSchema,
]);

export type TimelineFetchResponse = z.infer<typeof TimelineFetchResponseSchema>;

export const TimelineCliSummaryBoundarySchema = TimelineCliSummarySchema;

export type TimelineCliSummaryParsed = z.infer<typeof TimelineCliSummaryBoundarySchema>;

export const TimelineCliTweetBoundarySchema = TimelineCliTweetSchema;

export type TimelineCliTweetParsed = z.infer<typeof TimelineCliTweetBoundarySchema>;

export const TimelineCliResponseBoundarySchema = TimelineCliResponseSchema.transform((payload) => ({
  ...payload,
  tweets: payload.tweets.map((tweet) => TimelineCliTweetBoundarySchema.parse(tweet)),
  summary: TimelineCliSummaryBoundarySchema.parse(payload.summary),
}));

export type TimelineCliResponseParsed = z.infer<typeof TimelineCliResponseBoundarySchema>;
export type TimelineCliResponseInput = z.input<typeof TimelineCliResponseSchema>;

export function parseTimelineCliResponse(input: TimelineCliResponseInput): TimelineCliResponseParsed {
  return TimelineCliResponseBoundarySchema.parse(input);
}

export type TimelineSnapshotResponse = z.infer<typeof TimelineSnapshotResponseSchema>;
export type MentionsTimelineResponse = z.infer<typeof MentionsTimelineResponseSchema>;
export type UserStatusResponse = z.infer<typeof UserStatusResponseSchema>;

const TimelineLinkShareUserBoundarySchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable().optional(),
  verifiedType: VerifiedTypeSchema.nullable().optional(),
  influenceScore: z.number(),
  followersCount: z.number().int().min(0).nullable().optional(),
});

export const TimelineLinkShareUserSchema = TimelineLinkShareUserBoundarySchema.transform((user) => ({
  id: user.id,
  username: user.username,
  name: user.name,
  profileImageUrl: user.profileImageUrl ?? null,
  verifiedType: user.verifiedType ?? 'none',
  influenceScore: user.influenceScore,
  followersCount: user.followersCount ?? undefined,
}));

const TimelineLinkMentionBoundarySchema = z.object({
  id: z.string(),
  linkUrl: z.string(),
  createdAt: z.date(),
  sharedBy: TimelineLinkShareUserSchema.nullable(),
  contentAuthor: TimelineLinkShareUserSchema.nullable(),
  text: z.string(),
  rawText: z.string(),
  timelineTweetId: z.string(),
  contentTweetId: z.string(),
  likeCount: z.number().int().min(0),
  replyCount: z.number().int().min(0),
  retweetCount: z.number().int().min(0),
  quoteCount: z.number().int().min(0),
  viewCount: z.number().int().min(0).optional(),
  commentText: z.string().nullable().optional(),
  sharedContentText: z.string().nullable().optional(),
  hasComment: z.boolean().optional(),
  media: z.array(
    z.object({
      mediaKey: z.string(),
      type: z.enum(['photo', 'video', 'animated_gif']),
      url: z.string().optional(),
      previewImageUrl: z.string().optional(),
    })
  ),
  isRetweet: z.boolean(),
  isQuote: z.boolean(),
});

export const TimelineLinkMentionSchema = TimelineLinkMentionBoundarySchema.transform((mention) => {
  const normalizedComment =
    typeof mention.commentText === 'string' && mention.commentText.length > 0 ? mention.commentText : null;
  const normalizedSharedContent =
    typeof mention.sharedContentText === 'string' && mention.sharedContentText.length > 0
      ? mention.sharedContentText
      : null;

  return {
    ...mention,
    commentText: normalizedComment,
    sharedContentText: normalizedSharedContent,
    hasComment: mention.hasComment ?? Boolean(normalizedComment),
    viewCount: mention.viewCount ?? 0,
  };
});

export const TimelineLinkInsightSchema = z.object({
  id: z.string(),
  canonicalUrl: z.string(),
  displayHost: z.string(),
  displayPath: z.string(),
  frequency: z.number(),
  uniqueSharers: z.number(),
  totalInfluence: z.number(),
  averageInfluence: z.number(),
  topInfluencer: TimelineLinkShareUserSchema.nullable().optional(),
  metadata: z.unknown().nullable().optional(),
  mentions: z.array(TimelineLinkMentionSchema),
});

export const TimelineInsightSourceBreakdownSchema = z.object({
  source: z.string(),
  totalTweetCount: z.number().int().min(0),
  uniqueTweetCount: z.number().int().min(0),
  linkedTweetCount: z.number().int().min(0).optional(),
});

export const TimelineLinkInsightsMetadataSchema = z.object({
  dataComplete: z.boolean(),
  coveragePercent: z.number(),
  needsSync: z.boolean(),
  tweetCount: z.number().nullable(),
  maxTweets: z.number().nullable(),
  oldestDataPoint: z.date().nullable(),
  newestDataPoint: z.date().nullable(),
  refining: z.boolean().optional(),
  totalLinks: z.number().int().min(0).optional(),
  selfReplyLinkCount: z.number().int().min(0).optional(),
  selfReplyLinkRoots: z.number().int().min(0).optional(),
  selfReplyLinkMentions: z.number().int().min(0).optional(),
  sourceBreakdown: z.array(TimelineInsightSourceBreakdownSchema).optional(),
  overlapTweetCount: z.number().int().min(0).optional(),
});

export const TimelineLinkInsightsPayloadSchema = z.object({
  links: z.array(TimelineLinkInsightSchema),
  metadata: TimelineLinkInsightsMetadataSchema,
});

export const TimelineLinksRequestSchema = z.object({
  timeRange: TimeRangeSchema.default('24h'),
  maxInsights: z.number().int().min(1).max(200).optional(),
  maxMetadataDepth: z.number().int().min(0).max(6).optional(),
  refine: z.boolean().optional(),
});

const TimelineVideoVariantSchema = z.object({
  url: z.string(),
  contentType: z.string(),
  bitRate: z.number().int().min(0).nullable().optional(),
});

const TimelineVideoMentionBoundarySchema = z.object({
  id: z.string(),
  mediaKey: z.string(),
  createdAt: z.date(),
  sharedBy: TimelineLinkShareUserSchema.nullable(),
  contentAuthor: TimelineLinkShareUserSchema.nullable(),
  text: z.string(),
  rawText: z.string(),
  timelineTweetId: z.string(),
  contentTweetId: z.string(),
  likeCount: z.number().int().min(0),
  replyCount: z.number().int().min(0),
  retweetCount: z.number().int().min(0),
  quoteCount: z.number().int().min(0),
  viewCount: z.number().int().min(0).optional(),
  commentText: z.string().nullable().optional(),
  sharedContentText: z.string().nullable().optional(),
  hasComment: z.boolean().optional(),
  previewImageUrl: z.string().nullable().optional(),
  durationMs: z.number().int().min(0).nullable().optional(),
  variants: z.array(TimelineVideoVariantSchema).optional(),
  isRetweet: z.boolean(),
  isQuote: z.boolean(),
});

export const TimelineVideoMentionSchema = TimelineVideoMentionBoundarySchema.transform((mention) => {
  const normalizedComment =
    typeof mention.commentText === 'string' && mention.commentText.length > 0 ? mention.commentText : null;
  const normalizedSharedContent =
    typeof mention.sharedContentText === 'string' && mention.sharedContentText.length > 0
      ? mention.sharedContentText
      : null;

  return {
    ...mention,
    commentText: normalizedComment,
    sharedContentText: normalizedSharedContent,
    hasComment: mention.hasComment ?? Boolean(normalizedComment),
    viewCount: mention.viewCount ?? 0,
    variants: toArray(mention.variants),
  };
});

export const TimelineVideoInsightSchema = z.object({
  id: z.string(),
  mediaKey: z.string(),
  previewImageUrl: z.string().nullable(),
  durationMs: z.number().int().min(0).nullable(),
  frequency: z.number(),
  uniqueSharers: z.number(),
  totalInfluence: z.number(),
  averageInfluence: z.number(),
  topInfluencer: TimelineLinkShareUserSchema.nullable().optional(),
  variants: z.array(TimelineVideoVariantSchema),
  mentions: z.array(TimelineVideoMentionSchema),
});

export const TimelineVideoInsightsPayloadSchema = z.object({
  videos: z.array(TimelineVideoInsightSchema),
  metadata: TimelineLinkInsightsMetadataSchema,
});

export const TimelineVideosRequestSchema = z.object({
  timeRange: TimeRangeSchema.default('24h'),
  maxInsights: z.number().int().min(1).max(200).optional(),
});

export const TimelineRelationshipStatusSchema = z.object({
  type: z.enum(['mutual', 'following', 'followed_by', 'none']),
  label: z.string(),
  shortLabel: z.string(),
  statusMask: z.number().int().min(0),
  isMutual: z.boolean(),
});

export const TimelineRelationshipsRequestSchema = z.object({
  profileIds: z.array(z.string()),
});

export const TimelineRelationshipsResponseSchema = z.object({
  relationships: z.record(z.string(), TimelineRelationshipStatusSchema),
  userId: z.string(),
});

export const TimelineLastSeenResponseSchema = z.object({
  id: z.string().nullable(),
  at: z.string().nullable(),
});

export const TimelineLastSeenUpdateRequestSchema = z.object({
  id: z.string().min(1),
  at: z.string().nullable().optional(),
});

export const TimelineLastSeenUpdateResponseSchema = z.object({
  ok: z.literal(true),
});

export const TimelineInsightsSyncRequestSchema = z.object({
  timeRange: TimeRangeSchema.default('24h'),
});

export const TimelineInsightsSyncQuerySchema = z.object({
  timeRange: TimeRangeSchema.optional(),
});
export type TimelineInsightsSyncQuery = z.infer<typeof TimelineInsightsSyncQuerySchema>;

export const TimelineInsightsSyncResponseSchema = z.object({
  success: z.literal(true),
  started: z.boolean(),
  jobId: z.string().optional(),
  reason: z.string().optional(),
});

const EMAIL_DELIVERY_STATUS_VALUES = ['reserved', 'pending', 'processing', 'sent', 'failed', 'cancelled'] as const;

export const TimelineDigestDeliverySchema = z.object({
  id: z.string(),
  messageId: z.string().nullable(),
  sentAt: NullableIsoDateCodec,
  deliveryType: z.string(),
  status: z.enum(EMAIL_DELIVERY_STATUS_VALUES),
  deliveryStatus: z.enum(EMAIL_DELIVERY_STATUS_VALUES),
  errorSummary: z.string().nullable(),
});

export const TimelineDigestLastResponseSchema = z.object({
  last: TimelineDigestDeliverySchema.nullable(),
});

export const TimelineDigestSendNowResponseSchema = z.object({
  enqueued: z.literal(true),
});

const TIMELINE_SYNC_TIME_RANGES = ['3h', '6h', '12h', '24h', '3d', '7d'] as const;

export const TimelineDigestSendNowRequestSchema = z.object({}).strict();

export const TimelineSyncRequestSchema = z.object({
  timeRange: z.enum(TIMELINE_SYNC_TIME_RANGES).default('24h'),
  fromTimestamp: z
    .date()
    .nullish()
    .transform((value) => (value == null ? null : value)),
  minTweets: z.number().int().min(1).max(10_000).optional(),
  force: z.boolean().optional(),
  reason: z.string().trim().min(1).max(160).optional(),
});

// tRPC input schema (same as request for POST)
export const TimelineSyncInputSchema = TimelineSyncRequestSchema;
export type TimelineSyncInput = z.infer<typeof TimelineSyncInputSchema>;

export const TimelineSyncResponseSchema = z.object({
  success: z.boolean(),
  status: z.enum(['started', 'already_complete', 'error', 'failed']),
  jobId: z.string().optional(),
  message: z.string(),
});

export const TimelineStatsQuerySchema = z.object({
  action: z.literal('stats'),
});

// Aggregated metrics returned by admin timeline stats dashboards.
export const TimelineStatsTopAuthorSchema = z.object({
  authorId: z.string(),
  username: z.string().nullable().optional(),
  tweetCount: z.number().int().nonnegative(),
  engagementRate: z.number().optional(),
});

export const TimelineStatsTopHashtagSchema = z.object({
  tag: z.string(),
  count: z.number().int().nonnegative(),
});

export const TimelineStatsResponseSchema = z.object({
  totalTweets: z.number(),
  todayTweets: z.number(),
  avgDailyTweets: z.number(),
  topAuthors: z.array(TimelineStatsTopAuthorSchema),
  topHashtags: z.array(TimelineStatsTopHashtagSchema),
  engagementRate: z.number(),
});

export type TimelineRequest = z.infer<typeof TimelineRequestSchema>;
export type TimelineFilterInput = z.infer<typeof TimelineFilterInputSchema>;
export type TimelineMentionProfile = z.infer<typeof TimelineMentionProfileSchema>;
export type TimelinePageResponse = z.infer<typeof TimelinePageResponseSchema>;
export type TimelineSnapshotStatus = z.infer<typeof TimelineSnapshotStatusSchema>;
export type TimelineNormalizedResponse = z.infer<typeof TimelineNormalizedResponseSchema>;
export type TimelineResponsePayload = z.infer<typeof TimelineResponsePayloadSchema>;
export type TimelineIncludes = z.infer<typeof TimelineIncludesSchema>;
export type TimelineRealtimeEnvelope = z.infer<typeof TimelineRealtimeEnvelopeSchema>;
export type TimelineMediaObject = z.infer<typeof TimelineMediaObjectSchema>;
export type TimelineMediaVariant = z.infer<typeof TimelineMediaVariantSchema>;
export type TimelineMediaInput = z.input<typeof TimelineMediaObjectInputSchema>;
export type TimelineCliTweet = z.infer<typeof TimelineCliTweetSchema>;
export type TimelineCliSummary = z.infer<typeof TimelineCliSummarySchema>;
export type TimelineCliPagination = z.infer<typeof TimelineCliPaginationSchema>;
export type TimelineCliResponse = z.infer<typeof TimelineCliResponseSchema>;
export type TimelineVariant = (typeof TIMELINE_VARIANTS)[number];
export type TimelineLinksRequest = z.infer<typeof TimelineLinksRequestSchema>;
export type TimelineLinkInsightsPayload = z.infer<typeof TimelineLinkInsightsPayloadSchema>;
export type TimelineLinkInsight = z.infer<typeof TimelineLinkInsightSchema>;
export type TimelineLinkMention = z.infer<typeof TimelineLinkMentionSchema>;
export type TimelineLinkShareUser = z.infer<typeof TimelineLinkShareUserSchema>;
export type TimelineLinkInsightsMetadata = z.infer<typeof TimelineLinkInsightsMetadataSchema>;
export type TimelineVideoVariant = z.infer<typeof TimelineVideoVariantSchema>;
export type TimelineVideoMention = z.infer<typeof TimelineVideoMentionSchema>;
export type TimelineVideoInsight = z.infer<typeof TimelineVideoInsightSchema>;
export type TimelineVideoInsightsPayload = z.infer<typeof TimelineVideoInsightsPayloadSchema>;
export type TimelineVideosRequest = z.infer<typeof TimelineVideosRequestSchema>;
export type TimelineLastSeenResponse = z.infer<typeof TimelineLastSeenResponseSchema>;
export type TimelineLastSeenUpdateRequest = z.infer<typeof TimelineLastSeenUpdateRequestSchema>;
export type TimelineLastSeenUpdateResponse = z.infer<typeof TimelineLastSeenUpdateResponseSchema>;
export type TimelineInsightsSyncRequest = z.infer<typeof TimelineInsightsSyncRequestSchema>;
export type TimelineInsightsSyncResponse = z.infer<typeof TimelineInsightsSyncResponseSchema>;
export type TimelineRelationshipStatus = z.infer<typeof TimelineRelationshipStatusSchema>;
export type TimelineRelationshipsRequest = z.infer<typeof TimelineRelationshipsRequestSchema>;
export type TimelineRelationshipsResponse = z.infer<typeof TimelineRelationshipsResponseSchema>;
export type TimelineDigestDelivery = z.infer<typeof TimelineDigestDeliverySchema>;
export type TimelineDigestLastResponse = z.infer<typeof TimelineDigestLastResponseSchema>;
export type TimelineDigestSendNowResponse = z.infer<typeof TimelineDigestSendNowResponseSchema>;
export type TimelineDigestSendNowRequest = z.infer<typeof TimelineDigestSendNowRequestSchema>;
export type TimelineSyncRequest = z.infer<typeof TimelineSyncRequestSchema>;
export type TimelineSyncResponse = z.infer<typeof TimelineSyncResponseSchema>;
export type TimelineStatsQuery = z.infer<typeof TimelineStatsQuerySchema>;
export type TimelineStatsTopAuthor = z.infer<typeof TimelineStatsTopAuthorSchema>;
export type TimelineStatsTopHashtag = z.infer<typeof TimelineStatsTopHashtagSchema>;
export type TimelineStatsResponse = z.infer<typeof TimelineStatsResponseSchema>;
